import { useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { useApi } from "./use-api";

export type CtaStepType = "soft_mention" | "reveal" | "urgency" | "bonus_stack" | "last_chance" | "closed";

export interface CtaBonus {
  name: string;
  valueCents: number;
}

export interface CtaStep {
  stepType: CtaStepType;
  triggerAtSeconds: number;
  config: {
    headline?: string;
    description?: string;
    productImageUrl?: string;
    originalPriceCents?: number;
    offerPriceCents?: number;
    currency?: string;
    countdownSeconds?: number;
    scarcityCount?: number;
    scarcityMode?: "real" | "simulated";
    bonuses?: CtaBonus[];
    ctaLabel?: string;
    ctaUrl?: string;
  };
}

export interface CtaSequence {
  id: string;
  webinarId: string;
  name: string;
  steps: CtaStep[];
  isActive: boolean;
  lastTriggeredStepIndex: number;
  totalImpressions: number;
  totalClicks: number;
  totalConversions: number;
  totalRevenueCents: number;
  conversionRate: number;
  createdAt: string;
  updatedAt: string;
}

export interface SaveCtaSequenceInput {
  name: string;
  steps: CtaStep[];
  isActive?: boolean;
}

interface TriggerNextResult {
  sequence: CtaSequence;
  cta: {
    id: string;
    sequenceId: string;
    stepIndex: number;
    stepType: CtaStepType;
    label: string;
    headline: string;
    description?: string | null;
    url?: string | null;
    durationSeconds?: number | null;
  };
}

export function useCtaSequence(webinarId: string | undefined) {
  return useApi<CtaSequence[]>(
    webinarId ? `/v1/webinars/${webinarId}/cta-sequences` : "",
  );
}

export function useCreateCtaSequence(webinarId: string | undefined) {
  const mutate = useCallback(async (input: SaveCtaSequenceInput) => {
    if (!webinarId) {
      throw new Error("Missing webinar ID");
    }

    const result = await apiFetch<{ data: CtaSequence }>(
      `/v1/webinars/${webinarId}/cta-sequences`,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );

    return result.data;
  }, [webinarId]);

  return { mutate };
}

export function useUpdateCtaSequence(webinarId: string | undefined, sequenceId: string | undefined) {
  const mutate = useCallback(async (input: SaveCtaSequenceInput) => {
    if (!webinarId || !sequenceId) {
      throw new Error("Missing CTA sequence context");
    }

    const result = await apiFetch<{ data: CtaSequence }>(
      `/v1/webinars/${webinarId}/cta-sequences/${sequenceId}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
      },
    );

    return result.data;
  }, [sequenceId, webinarId]);

  return { mutate };
}

export function useTriggerNextCtaStep(webinarId: string | undefined, sequenceId: string | undefined) {
  const mutate = useCallback(async () => {
    if (!webinarId || !sequenceId) {
      throw new Error("Missing CTA sequence context");
    }

    const result = await apiFetch<{ data: TriggerNextResult }>(
      `/v1/webinars/${webinarId}/cta-sequences/${sequenceId}/trigger-next`,
      {
        method: "POST",
      },
    );

    return result.data;
  }, [sequenceId, webinarId]);

  return { mutate };
}
